import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/customSupabaseClient';
import {
  AlertCircle,
  UserPlus,
  Clock,
  AlertTriangle,
  Dumbbell,
  RefreshCw,
  Smartphone,
  Globe,
  User,
  ChevronDown,
  ChevronUp,
  Mail,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { SendEmailButton } from '@/components/email/SendEmailButton';
import { cn } from '@/lib/utils';

const PLATFORM_CONFIG = {
  ios:     { label: 'iOS',     className: 'border-blue-500/20 bg-blue-500/10 text-blue-500' },
  android: { label: 'Android', className: 'border-green-500/20 bg-green-500/10 text-green-500' },
  stripe:  { label: 'Web',     className: 'border-purple-500/20 bg-purple-500/10 text-purple-500' },
  web:     { label: 'Web',     className: 'border-purple-500/20 bg-purple-500/10 text-purple-500' },
};

const DEVICE_CONFIG = {
  ios:     { label: 'iOS App',     className: 'border-blue-500/20 bg-blue-500/10 text-blue-500' },
  android: { label: 'Android App', className: 'border-green-500/20 bg-green-500/10 text-green-500' },
  web:     { label: 'Web Only',    className: 'border-purple-500/20 bg-purple-500/10 text-purple-500' },
};

function relativeTime(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function getUrgencyLevel(daysRemaining) {
  if (daysRemaining <= 2) return 'critical';
  if (daysRemaining <= 5) return 'warning';
  return 'healthy';
}

const URGENCY_STYLES = {
  critical: {
    border: 'border-l-red-500 border-l-4',
    progressColor: 'bg-red-500',
    badgeVariant: 'destructive',
    ring: 'ring-1 ring-red-500/20',
  },
  warning: {
    border: 'border-l-amber-500 border-l-4',
    progressColor: 'bg-amber-500',
    badgeVariant: 'warning',
    ring: '',
  },
  healthy: {
    border: 'border-l-emerald-500 border-l-4',
    progressColor: 'bg-emerald-500',
    badgeVariant: 'success',
    ring: '',
  },
};

const PageSkeleton = () => (
  <div className="space-y-6">
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {[...Array(4)].map((_, i) => (
        <Card key={i}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-4 rounded" />
          </CardHeader>
          <CardContent><Skeleton className="h-7 w-16" /></CardContent>
        </Card>
      ))}
    </div>
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <Card key={i}><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
      ))}
    </div>
  </div>
);

function KpiCard({ title, value, subtitle, icon: Icon, accent }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={cn('h-4 w-4', accent ? `text-${accent}-500` : 'text-muted-foreground')} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function SubscriberCard({ subscriber }) {
  const [expanded, setExpanded] = useState(false);
  const urgency = getUrgencyLevel(subscriber.days_remaining);
  const styles = URGENCY_STYLES[urgency];
  const elapsed = subscriber.trial_days_elapsed;
  const total = subscriber.trial_days_total;
  const progressPercent = Math.min(100, Math.round((elapsed / total) * 100));
  const platform = PLATFORM_CONFIG[subscriber.purchase_platform] || PLATFORM_CONFIG.web;

  return (
    <Card className={cn(styles.border, styles.ring, 'transition-all')}>
      <CardContent className="p-5">
        {/* Header: name, platform, subscribed time */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img
              className="h-9 w-9 rounded-full object-cover shrink-0"
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(subscriber.full_name || 'User')}&background=random&size=36`}
              alt=""
            />
            <div className="min-w-0">
              <Link
                to={`/user-activity/${subscriber.user_id}`}
                className="font-medium text-sm hover:underline underline-offset-2 truncate block"
              >
                {subscriber.full_name || 'Unknown'}
              </Link>
              <p className="text-xs text-muted-foreground truncate">{subscriber.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            <Badge className={platform.className}>Paid {platform.label}</Badge>
            {subscriber.last_platform ? (
              <Badge className={(DEVICE_CONFIG[subscriber.last_platform] || DEVICE_CONFIG.web).className}>
                <Smartphone className="h-3 w-3 mr-1" />
                {(DEVICE_CONFIG[subscriber.last_platform] || DEVICE_CONFIG.web).label}
              </Badge>
            ) : (
              <Badge variant="warning">
                <Smartphone className="h-3 w-3 mr-1" />No app yet
              </Badge>
            )}
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {relativeTime(subscriber.subscribed_at)}
            </span>
          </div>
        </div>

        {/* Trial timeline bar */}
        <div className="mt-4 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{formatDate(subscriber.trial_start)}</span>
            <span className="font-semibold">
              {subscriber.days_remaining === 0 ? (
                <span className="text-red-500">Expires today</span>
              ) : (
                <>
                  <span className={cn(
                    urgency === 'critical' && 'text-red-500',
                    urgency === 'warning' && 'text-amber-500',
                    urgency === 'healthy' && 'text-emerald-500',
                  )}>
                    {subscriber.days_remaining}
                  </span>
                  {' '}of {total} days left
                </>
              )}
            </span>
            <span className="text-muted-foreground">{formatDate(subscriber.trial_end)}</span>
          </div>
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className={cn('h-full rounded-full transition-all', styles.progressColor)}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Engagement signals */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Badge variant={subscriber.onboarding_complete ? 'success' : 'warning'}>
            {subscriber.onboarding_complete
              ? 'Onboarded'
              : `Step ${subscriber.onboarding_step}/5`}
          </Badge>

          <Badge variant={subscriber.sessions_completed > 0 ? 'success' : 'destructive'}>
            <Dumbbell className="h-3 w-3 mr-1" />
            {subscriber.sessions_completed > 0
              ? `${subscriber.sessions_completed} session${subscriber.sessions_completed !== 1 ? 's' : ''}`
              : 'No sessions'}
          </Badge>

          {subscriber.emails_sent_count > 0 && (
            <Badge className="border-sky-500/20 bg-sky-500/10 text-sky-600 dark:text-sky-400">
              <Mail className="h-3 w-3 mr-1" />
              Contacted{subscriber.emails_sent_count > 1 ? ` (${subscriber.emails_sent_count})` : ''}
            </Badge>
          )}

          {subscriber.cancel_at_period_end && (
            <Badge variant="destructive">
              <XCircle className="h-3 w-3 mr-1" />Cancelled
            </Badge>
          )}

          {subscriber.assigned_coach_id ? (
            <Badge variant="default">
              <User className="h-3 w-3 mr-1" />Coach assigned
            </Badge>
          ) : (
            <Badge variant="warning">
              <User className="h-3 w-3 mr-1" />No coach
            </Badge>
          )}

          {subscriber.last_sign_in_at && (
            <span className="text-xs text-muted-foreground">
              Last active: {relativeTime(subscriber.last_sign_in_at)}
            </span>
          )}

          {subscriber.language && (
            <span className="text-xs text-muted-foreground uppercase">
              {subscriber.language}
            </span>
          )}
        </div>

        {/* At-risk banners */}
        {subscriber.sessions_completed === 0 && subscriber.days_remaining <= 5 && (
          <div className="mt-3 rounded-md bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            Trial ending soon with no training sessions completed
          </div>
        )}
        {(!subscriber.last_platform || subscriber.last_platform === 'web') && (
          <div className="mt-2 rounded-md bg-blue-500/10 border border-blue-500/20 px-3 py-2 text-xs text-blue-600 dark:text-blue-400 flex items-center gap-2">
            <Smartphone className="h-3.5 w-3.5 shrink-0" />
            Web only — hasn't downloaded the mobile app yet. Recommend the app for a better experience.
          </div>
        )}

        {/* Actions + expand */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SendEmailButton
              userId={subscriber.user_id}
              fullName={subscriber.full_name}
              email={subscriber.email}
            />
            <Link to={`/user-activity/${subscriber.user_id}`}>
              <Button variant="outline" size="sm" className="h-7 text-xs">
                View Profile
              </Button>
            </Link>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <><ChevronUp className="h-3.5 w-3.5 mr-1" />Less</>
            ) : (
              <><ChevronDown className="h-3.5 w-3.5 mr-1" />Details</>
            )}
          </Button>
        </div>

        {/* Expandable details */}
        {expanded && (
          <div className="mt-3 pt-3 border-t grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-muted-foreground">Journey Type</p>
              <p className="font-medium capitalize">{subscriber.user_journey_type || '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Device</p>
              <p className="font-medium">{subscriber.last_device_model || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Trial Started</p>
              <p className="font-medium">{subscriber.trial_start ? new Date(subscriber.trial_start).toLocaleDateString() : '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Trial Ends</p>
              <p className="font-medium">{subscriber.trial_end ? new Date(subscriber.trial_end).toLocaleDateString() : '—'}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const SORT_OPTIONS = [
  { value: 'urgency', label: 'Most Urgent' },
  { value: 'newest', label: 'Newest First' },
  { value: 'least_engaged', label: 'Least Engaged' },
];

const PLATFORM_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'web', label: 'Web' },
];

export default function NewSubscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('urgency');
  const [platformFilter, setPlatformFilter] = useState('all');

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_get_trial_subscribers');
      if (rpcError) throw rpcError;
      setSubscribers(data || []);
    } catch (err) {
      setError('Failed to load trial subscribers.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  const filtered = useMemo(() => {
    let result = [...subscribers];

    if (platformFilter !== 'all') {
      result = result.filter(s => {
        if (platformFilter === 'web') return s.purchase_platform === 'stripe' || s.purchase_platform === 'web';
        return s.purchase_platform === platformFilter;
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(s =>
        (s.full_name || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q)
      );
    }

    if (sortBy === 'urgency') {
      result.sort((a, b) => a.days_remaining - b.days_remaining);
    } else if (sortBy === 'newest') {
      result.sort((a, b) => new Date(b.subscribed_at) - new Date(a.subscribed_at));
    } else if (sortBy === 'least_engaged') {
      result.sort((a, b) => {
        const scoreA = a.sessions_completed + (a.onboarding_complete ? 10 : 0);
        const scoreB = b.sessions_completed + (b.onboarding_complete ? 10 : 0);
        return scoreA - scoreB;
      });
    }

    return result;
  }, [subscribers, search, sortBy, platformFilter]);

  const kpis = useMemo(() => {
    const total = subscribers.length;
    const endingSoon = subscribers.filter(s => s.days_remaining <= 3).length;
    const notOnboarded = subscribers.filter(s => !s.onboarding_complete).length;
    const noSessions = subscribers.filter(s => s.sessions_completed === 0).length;
    const noApp = subscribers.filter(s => !s.last_platform || s.last_platform === 'web').length;

    const platforms = subscribers.reduce((acc, s) => {
      const key = (s.purchase_platform === 'stripe' || s.purchase_platform === 'web') ? 'web' : s.purchase_platform;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return { total, endingSoon, notOnboarded, noSessions, noApp, platforms };
  }, [subscribers]);

  const platformSplit = [
    kpis.platforms.ios && `${kpis.platforms.ios} iOS`,
    kpis.platforms.android && `${kpis.platforms.android} Android`,
    kpis.platforms.web && `${kpis.platforms.web} Web`,
  ].filter(Boolean).join(' / ') || 'None';

  return (
    <>
      <Helmet><title>New Subscribers - My Movement Space</title></Helmet>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">New Subscribers</h1>
          <p className="text-sm text-muted-foreground mt-1">
            All currently trialing subscribers across platforms. React, onboard, and communicate.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchSubscribers} disabled={loading}>
          <RefreshCw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {loading ? <PageSkeleton /> : error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 gap-3">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-sm font-medium text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchSubscribers}>Try again</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* KPI cards */}
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
            <KpiCard
              title="Total Trialing"
              value={kpis.total}
              subtitle={platformSplit}
              icon={UserPlus}
            />
            <KpiCard
              title="Ending Soon"
              value={kpis.endingSoon}
              subtitle="3 days or less remaining"
              icon={Clock}
              accent={kpis.endingSoon > 0 ? 'red' : undefined}
            />
            <KpiCard
              title="Not Onboarded"
              value={kpis.notOnboarded}
              subtitle="Haven't completed setup"
              icon={AlertTriangle}
              accent={kpis.notOnboarded > 0 ? 'amber' : undefined}
            />
            <KpiCard
              title="No Sessions"
              value={kpis.noSessions}
              subtitle="Haven't trained yet"
              icon={Dumbbell}
              accent={kpis.noSessions > 0 ? 'amber' : undefined}
            />
            <KpiCard
              title="No App"
              value={kpis.noApp}
              subtitle="Web only, no mobile app"
              icon={Smartphone}
              accent={kpis.noApp > 0 ? 'amber' : undefined}
            />
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="max-w-sm"
            />
            <div className="flex items-center gap-1">
              {PLATFORM_FILTERS.map(pf => (
                <Button
                  key={pf.value}
                  variant={platformFilter === pf.value ? 'default' : 'outline'}
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setPlatformFilter(pf.value)}
                >
                  {pf.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-1 sm:ml-auto">
              {SORT_OPTIONS.map(opt => (
                <Button
                  key={opt.value}
                  variant={sortBy === opt.value ? 'default' : 'ghost'}
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setSortBy(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Subscriber cards */}
          {filtered.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center h-48 gap-2">
                <UserPlus className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {subscribers.length === 0
                    ? 'No subscribers currently in trial period.'
                    : 'No subscribers match the current filters.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Showing {filtered.length} of {subscribers.length} trialing subscriber{subscribers.length !== 1 ? 's' : ''}
              </p>
              {filtered.map(sub => (
                <SubscriberCard key={sub.user_id} subscriber={sub} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
